import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import ModalParque, { SubModalSolicitarPipa, SubModalValidarParque } from "../components/ModalParque";
import "../styles/modal-parque.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Filtros disponibles. Por defecto: pendientes (sin_clasificar) — mismo comportamiento previo.
const FILTROS = {
  mis_parques:         { label: "Mis parques",        titulo: "Todos los parques de mi zona" },
  con_sistema_propio:  { label: "Con sistema propio", titulo: "Parques con sistema de riego propio (no requieren pipa)" },
  sin_clasificar:      { label: "Sin clasificar",     titulo: "Pendientes de validar en campo" },
  zonas:               { label: "Zonas asignadas",    titulo: "Parques agrupados por zona" },
};

export default function AreasContratadasModule() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ zonas: [], espacios: [] });
  const [filtro, setFiltro] = useState("sin_clasificar"); // default: pendientes
  const [parqueAbierto, setParqueAbierto] = useState(null);
  const [parqueSolicitar, setParqueSolicitar] = useState(null);
  const [parqueValidar, setParqueValidar] = useState(null);

  // Solo supervisor_areas y admin pueden abrir el modal (H1 spec)
  const puedeAbrirModal = user?.rol === "supervisor_areas" || user?.rol === "admin";
  const abrirParque = (parque) => { if (puedeAbrirModal) setParqueAbierto(parque); };
  const solicitarPipaPara = (parque) => { if (puedeAbrirModal) setParqueSolicitar(parque); };
  const validarParque = (parque) => { if (puedeAbrirModal) setParqueValidar(parque); };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/zonas`),
      axios.get(`${API}/espacios?limit=2000`),
    ]).then(([z, e]) => setData({ zonas: z.data, espacios: e.data }));
  }, []);

  const tieneZona = user?.rol === "supervisor_areas";
  const miZona = user?.zona_id;
  const zonasSec = user?.zonas_secundarias || [];
  const zonasAVer = tieneZona ? [miZona, ...zonasSec].filter(Boolean) : data.zonas.map(z => z.zona_id);

  // SEGURIDAD: estos 3 conjuntos están SIEMPRE filtrados por la zona del supervisor.
  const espaciosDeMiZona   = useMemo(() => tieneZona ? data.espacios.filter(e => zonasAVer.includes(e.zona_id)) : data.espacios, [data.espacios, tieneZona, zonasAVer]);
  const conSistemaPropio   = useMemo(() => espaciosDeMiZona.filter(e => (e.requiere_pipa || "").startsWith("NO REQUIERE")), [espaciosDeMiZona]);
  const sinClasificar      = useMemo(() => espaciosDeMiZona.filter(e => !e.requiere_pipa), [espaciosDeMiZona]);

  const filasFiltradas = useMemo(() => {
    switch (filtro) {
      case "mis_parques":        return espaciosDeMiZona;
      case "con_sistema_propio": return conSistemaPropio;
      case "sin_clasificar":     return sinClasificar;
      case "zonas":              return espaciosDeMiZona; // agrupado por zona en render
      default:                   return sinClasificar;
    }
  }, [filtro, espaciosDeMiZona, conSistemaPropio, sinClasificar]);

  const tituloSeccion = FILTROS[filtro]?.titulo || "Pendientes de validar en campo";
  const esFiltroNoDefault = filtro !== "sin_clasificar";

  // Para vista "zonas": agrupar por zona_id
  const filasAgrupadas = useMemo(() => {
    if (filtro !== "zonas") return null;
    const grupos = {};
    filasFiltradas.forEach(e => {
      const z = e.zona_id || "—";
      if (!grupos[z]) grupos[z] = [];
      grupos[z].push(e);
    });
    return Object.entries(grupos).sort(([a], [b]) => a.localeCompare(b));
  }, [filtro, filasFiltradas]);

  return (
    <div className="hub-bg" data-testid="page-areas">
      <header className="topnav">
        <div className="brand">
          <button onClick={() => navigate("/hub")} className="iconbtn mr-1" data-testid="btn-back"><i className="fa-solid fa-arrow-left"/></button>
          <div className="logo" style={{background:"#475569"}}><i className="fa-solid fa-circle-check"/></div>
          <div>
            <div className="title">Áreas Contratadas</div>
            <div className="sub">{tieneZona ? `Tu zona: ${zonasAVer.join(", ")}` : "Vista global"}</div>
          </div>
        </div>
        <div className="actions">
          <div className="hidden sm:block text-right mr-2"><div className="text-sm font-semibold">{user?.nombre}</div><div className="text-xs text-slate-500">Supervisor Áreas Contratadas</div></div>
          <button onClick={logout} className="btn-ghost" data-testid="btn-logout" style={{padding:"0.5rem 0.9rem"}}><i className="fa-solid fa-right-from-bracket mr-1"/>Salir</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-7">
        <h1 className="text-2xl font-bold leading-tight">Hola, {user?.nombre?.split(" ")[0]}</h1>
        <p className="text-slate-500 mt-1">Click en cualquier indicador para filtrar la tabla.</p>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 mb-6">
          <KpiClick testid="kpi-mis-parques"        label="Mis parques"        value={espaciosDeMiZona.length} icon="fa-tree"     color="var(--green)"  activo={filtro === "mis_parques"}        onClick={() => setFiltro("mis_parques")}/>
          <KpiClick testid="kpi-con-sistema-propio" label="Con sistema propio" value={conSistemaPropio.length} icon="fa-faucet"   color="var(--accent)" activo={filtro === "con_sistema_propio"} onClick={() => setFiltro("con_sistema_propio")}/>
          <KpiClick testid="kpi-sin-clasificar"     label="Sin clasificar"     value={sinClasificar.length}    icon="fa-question" color="var(--amber)"  activo={filtro === "sin_clasificar"}     onClick={() => setFiltro("sin_clasificar")}/>
          <KpiClick testid="kpi-zonas"              label="Zonas asignadas"    value={zonasAVer.length}        icon="fa-map"      color="var(--purple)" activo={filtro === "zonas"}              onClick={() => setFiltro("zonas")}/>
        </section>

        <section className="card-base">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="text-lg font-bold" data-testid="tabla-titulo">{tituloSeccion}</div>
              <p className="text-sm text-slate-500 mt-1" data-testid="tabla-conteo">
                Mostrando <strong>{filasFiltradas.length}</strong> {filasFiltradas.length === 1 ? "parque" : "parques"}
                {tieneZona && <> de tu zona ({zonasAVer.join(", ")})</>}.
              </p>
            </div>
            {esFiltroNoDefault && (
              <button
                onClick={() => setFiltro("sin_clasificar")}
                className="btn-ghost shrink-0"
                style={{padding:"0.45rem 0.9rem", fontSize:"0.85rem"}}
                data-testid="btn-limpiar-filtro"
              >
                <i className="fa-solid fa-xmark mr-1"/>Limpiar filtro
              </button>
            )}
          </div>

          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-sm" data-testid="tabla-areas">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-3">ID</th>
                  <th className="text-left p-3">Nombre</th>
                  <th className="text-left p-3">Zona</th>
                  <th className="text-left p-3">Colonia</th>
                  <th className="text-left p-3">GPS</th>
                  {puedeAbrirModal && <th className="text-center p-3">Acción</th>}
                </tr>
              </thead>
              <tbody>
                {filtro === "zonas" && filasAgrupadas ? (
                  filasAgrupadas.map(([zonaId, filas]) => (
                    <ZonaGrupo key={zonaId} zonaId={zonaId} filas={filas} onClick={abrirParque} onSolicitar={solicitarPipaPara} onValidar={validarParque} clicable={puedeAbrirModal}/>
                  ))
                ) : (
                  filasFiltradas.map(e => <FilaParque key={e.id} e={e} onClick={abrirParque} onSolicitar={solicitarPipaPara} onValidar={validarParque} clicable={puedeAbrirModal}/>)
                )}
                {filasFiltradas.length === 0 && (
                  <tr><td colSpan={puedeAbrirModal ? 6 : 5} className="p-6 text-center text-slate-400 text-sm">No hay parques en este filtro.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {puedeAbrirModal ? (
            <div className="mt-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <i className="fa-solid fa-hand-pointer mr-1"/> <strong>Click en un parque</strong> para ver su información y acciones disponibles.
            </div>
          ) : (
            <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <strong>Vista de solo lectura.</strong> Acciones sobre parques (validar · reportar · cambiar tipo de riego) están reservadas al supervisor de áreas y al administrador.
            </div>
          )}
        </section>
      </main>

      <ModalParque
        parque={parqueAbierto}
        onClose={() => setParqueAbierto(null)}
        onParqueActualizado={(esp) => {
          setData(prev => ({
            ...prev,
            espacios: prev.espacios.map(e => e.id === esp.id ? { ...e, ...esp } : e),
          }));
          setParqueAbierto(prev => prev && prev.id === esp.id ? { ...prev, ...esp } : prev);
        }}
      />

      {parqueSolicitar && (
        <SubModalSolicitarPipa
          parque={parqueSolicitar}
          onClose={() => setParqueSolicitar(null)}
        />
      )}

      {parqueValidar && (
        <SubModalValidarParque
          parque={parqueValidar}
          onClose={() => setParqueValidar(null)}
          onValidado={(esp) => {
            setData(prev => ({
              ...prev,
              espacios: prev.espacios.map(e => e.id === esp.id ? { ...e, ...esp } : e),
            }));
          }}
        />
      )}
    </div>
  );
}

function FilaParque({ e, onClick, onSolicitar, onValidar, clicable }) {
  const handleClick = clicable ? () => onClick(e) : undefined;
  return (
    <tr
      className={`border-t border-slate-100 hover:bg-slate-50 ${clicable ? "row-parque-clickable" : ""}`}
      data-testid={`row-${e.id_oficial}`}
      onClick={handleClick}
    >
      <td className="p-3 mono text-xs">{e.id_oficial}</td>
      <td className="p-3 font-medium">
        {e.nombre}
        {e.prioridad && (
          <span
            className="pill-tiny ml-2"
            style={{
              background: e.prioridad === "ALTA" ? "#FEE2E2" : e.prioridad === "MEDIA" ? "#FEF3C7" : "#DCFCE7",
              color:      e.prioridad === "ALTA" ? "#B91C1C" : e.prioridad === "MEDIA" ? "#B45309" : "#15803D",
              fontSize: "0.65rem",
              fontWeight: 700,
            }}
            data-testid={`pill-prioridad-${e.id_oficial}`}
          >{e.prioridad}</span>
        )}
        {e.estado_sistema_riego === "FUERA_DE_SERVICIO" && (
          <span
            className="pill-tiny ml-1"
            style={{ background: "#FEF3C7", color: "#92400E", fontSize: "0.65rem", fontWeight: 700 }}
            title="Sistema fuera de servicio"
            data-testid={`pill-fuera-servicio-${e.id_oficial}`}
          ><i className="fa-solid fa-triangle-exclamation mr-1"/>FUERA SERV.</span>
        )}
      </td>
      <td className="p-3"><span className="pill-tiny pill-zone">{e.zona_id}</span></td>
      <td className="p-3 text-slate-500">{e.colonia || "—"}</td>
      <td className="p-3">
        {e.gps_lat && e.gps_lon ? (
          <a
            href={`https://www.google.com/maps?q=${e.gps_lat},${e.gps_lon}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-700 hover:underline mono"
            onClick={(ev) => ev.stopPropagation()}
          ><i className="fa-solid fa-location-dot"/> Ver mapa</a>
        ) : <span className="text-xs text-slate-400">—</span>}
      </td>
      {clicable && (
        <td className="p-3 text-center" style={{whiteSpace:"nowrap"}}>
          <button
            type="button"
            className="row-validar-btn"
            title="Validar parque en campo"
            aria-label={`Validar ${e.nombre}`}
            onClick={(ev) => { ev.stopPropagation(); onValidar(e); }}
            data-testid={`btn-validar-fila-${e.id_oficial}`}
            style={{
              background: "#FFFFFF",
              border: "1px solid #10B981",
              color: "#047857",
              padding: "0.3rem 0.65rem",
              borderRadius: 7,
              fontSize: "0.78rem",
              fontWeight: 700,
              cursor: "pointer",
              marginRight: 4,
            }}
          >
            <i className="fa-solid fa-circle-check mr-1"/>Validar
          </button>
          <button
            type="button"
            className="row-solicitar-btn"
            title="Solicitar pipa para este parque"
            aria-label={`Solicitar pipa para ${e.nombre}`}
            onClick={(ev) => { ev.stopPropagation(); onSolicitar(e); }}
            data-testid={`btn-solicitar-fila-${e.id_oficial}`}
          >
            <i className="fa-solid fa-truck-droplet"/>
          </button>
        </td>
      )}
    </tr>
  );
}

function ZonaGrupo({ zonaId, filas, onClick, onSolicitar, onValidar, clicable }) {
  const colSpan = clicable ? 6 : 5;
  return (
    <>
      <tr className="bg-slate-100" data-testid={`grupo-zona-${zonaId}`}>
        <td colSpan={colSpan} className="p-2 text-xs font-bold uppercase tracking-wider text-slate-700">
          <i className="fa-solid fa-map mr-1.5"/>{zonaId} · {filas.length} {filas.length === 1 ? "parque" : "parques"}
        </td>
      </tr>
      {filas.map(e => <FilaParque key={e.id} e={e} onClick={onClick} onSolicitar={onSolicitar} onValidar={onValidar} clicable={clicable}/>)}
    </>
  );
}

function KpiClick({ label, value, icon, color, activo, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={activo ? "true" : "false"}
      className="card-base text-left transition-all"
      style={{
        cursor: "pointer",
        outline: activo ? `2px solid ${color}` : "none",
        outlineOffset: activo ? "2px" : "0",
        background: activo ? "rgba(15, 23, 42, 0.03)" : undefined,
        transform: activo ? "translateY(-1px)" : undefined,
      }}
    >
      <div className="flex items-center justify-between">
        <i className={`fa-solid ${icon} text-lg`} style={{color}}/>
        {activo && (
          <span className="pill-tiny" style={{background: color, color: "white", fontSize: "0.65rem"}} data-testid={`${testid}-active-badge`}>
            FILTRO ACTIVO
          </span>
        )}
      </div>
      <div className="text-2xl font-bold mt-1" style={{color}}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </button>
  );
}
